import { Button, Col, Form, Input, Row, message } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useResetPasswordMutation,
} from "../../query/admin.query";
import util from "../../utils/util";
import { useEffect } from "react";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
const [canResend, setCanResend] = useState(false);

  const [passwords, setPasswords] = useState({
    password: "",
    confirmPassword: "",
  });

  const sendOtpMutation = useSendOtpMutation();
  const verifyOtpMutation = useVerifyOtpMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  const sendOtp = () => {
    if (!email) {
      return message.error("Please enter email");
    }

    sendOtpMutation.mutate(
      { email },
      {
        onSuccess: () => {
          message.success("OTP sent successfully");
          setStep(2);
        },
        onError: (err) => {
          message.error(err?.message || "Failed to send OTP");
        },
      }
    );
  };

  const verifyOtp = () => {
    if (!otp) {
      return message.error("Please enter OTP");
    }

    verifyOtpMutation.mutate(
      {
        email,
        otp,
      },
      {
        onSuccess: (res) => {
          message.success("OTP verified");
            util.setUserData(res.data);    
          setStep(3);
        },
        onError: (err) => {
          message.error(err?.message || "Invalid OTP");
        },
      }
    );
  };

  const resetPassword = () => {
    if (!passwords.password) {
      return message.error("Please enter password");
    }

    if (passwords.password !== passwords.confirmPassword) {
      return message.error("Passwords do not match");
    }

    resetPasswordMutation.mutate(
      {
  newPassword: passwords.password,
},
      {
        onSuccess: () => {
          message.success("Password reset successfully");

          navigate("/login");
        },

        onError: (err) => {
          message.error(err?.message || "Failed to reset password");
        },
      }
    );
  };

  const resendOtp = () => {
  if (!canResend) return;

  sendOtpMutation.mutate(
    { email },
    {
      onSuccess: () => {
        message.success("OTP resent successfully");
        setCanResend(false);
        setResendTimer(30);
      },
      onError: (err) => {
        message.error(err?.message || "Failed to resend OTP");
      },
    }
  );
};

  useEffect(() => {
  let interval;

  if (step === 2 && !canResend) {
    setResendTimer(30);

    interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  return () => clearInterval(interval);
}, [step, canResend]);

  return (
    <div className="bg-gray-100 flex justify-center items-center h-screen">
      <div className="w-9/12 hidden animated-background h-screen bg-gradient-to-br from-[#D7C28D] via-[#c4a574] to-[#977731] lg:flex justify-center flex-col px-8 relative">
        <div className="flex items-center gap-x-2">
          <img
            src="/logo.png"
            alt="logo"
            className="w-[300px] h-[100px] object-contain"
          />
        </div>

        <p className="text-5xl font-medium my-4 text-white">
          Welcome,
        </p>

        <p className="text-5xl font-medium my-3 text-white">
          Reset Your Password 🔐
        </p>

        <p className="text-center absolute bottom-0 w-full mb-2 text-[1rem] text-[#1D1D1D]">
          © 2024 Pruden. All right reserved
        </p>
      </div>

      <div className="lg:p-36 md:p-52 sm:20 p-8 w-full lg:w-1/2">
        {step === 1 && (
          <>
            <h1 className="text-2xl font-semibold mb-2">
              Forgot Password
            </h1>

            <p className="text-gray-500 mb-6">
              Enter your email address to receive an OTP.
            </p>

            <Form
              layout="vertical"
              size="large"
              onSubmitCapture={sendOtp}
            >
              <Row gutter={[12, 2]}>
                <Col span={24}>
                  <Form.Item>
                    <Input
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) =>
                        setEmail(e.target.value)
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={sendOtpMutation.isPending}
                  >
                    Send OTP
                  </Button>
                </Col>

                <Col span={24} className="mt-4">
                  <Link
                    to="/login"
                    className="text-[#977731] font-medium"
                  >
                    ← Back to Login
                  </Link>
                </Col>
              </Row>
            </Form>
          </>
        )}

{step === 2 && (
  <>
    <div className="flex items-center gap-2 mb-4">
  <button
    onClick={() => setStep(1)}
    className="text-[#977731] font-medium"
  >
    ← Back
  </button>
</div>

    <h1 className="text-2xl font-semibold mb-2">
      Verify OTP
    </h1>

    <p className="text-gray-500 mb-6">
      Enter the OTP sent to {email}
    </p>

    <Form
      layout="vertical"
      size="large"
      onSubmitCapture={verifyOtp}
    >
      <Row gutter={[12, 2]}>
        <Col span={24}>
          <Form.Item>
            <Input
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={verifyOtpMutation.isPending}
          >
            Verify OTP
          </Button>
        </Col>

        <Col span={24} className="text-center mt-4">
          {!canResend ? (
            <p className="text-gray-500">
              Resend OTP in {resendTimer}s
            </p>
          ) : (
            <Button
              type="link"
              onClick={resendOtp}
              className="text-[#977731]"
            >
              Resend OTP
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  </>
)}

        {step === 3 && (
          <>
            <h1 className="text-2xl font-semibold mb-2">
              Set New Password
            </h1>

            <p className="text-gray-500 mb-6">
              Enter your new password.
            </p>

            <Form
              layout="vertical"
              size="large"
              onSubmitCapture={resetPassword}
            >
              <Row gutter={[12, 2]}>
                <Col span={24}>
                  <Form.Item>
                    <Input.Password
                      placeholder="New Password"
                      value={passwords.password}
                      onChange={(e) =>
                        setPasswords({
                          ...passwords,
                          password: e.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item>
                    <Input.Password
                      placeholder="Confirm Password"
                      value={passwords.confirmPassword}
                      onChange={(e) =>
                        setPasswords({
                          ...passwords,
                          confirmPassword:
                            e.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={resetPasswordMutation.isPending}
                  >
                    Reset Password
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </div>
    </div>
  );
}